import { useEffect, useLayoutEffect, useState } from "react";

const UNKNOWN_AXIS_GROUP = "UNKNOWN";

// Normalize across casing and separators so "Duration"/"DURATION"/"duration" collapse
// to the same bucket — same target type from different ingest paths shouldn't trigger
// the mixed-types restriction.
const normalizeProgressType = (pt: string) => pt.toLowerCase().replace(/[\s_-]/g, "");
const PERCENT_AXIS_FORMS = new Set(["trial", "interval", "taskanalysis", "scale"]);
// Distinct axes from SocialInteractionChart — Duration, Frequency, Rate each get their own.
// Anything else falls through to UNKNOWN so two targets with unrecognized types (both rendering
// on the same default "Actions" axis) aren't falsely treated as mixed.
const KNOWN_NON_PERCENT_FORMS = new Set(["duration", "frequency", "rate"]);

const axisGroupOf = (pt: string | null | undefined): string => {
  if (!pt) return UNKNOWN_AXIS_GROUP;
  const norm = normalizeProgressType(pt);
  if (PERCENT_AXIS_FORMS.has(norm)) return "PERCENT";
  if (KNOWN_NON_PERCENT_FORMS.has(norm)) return norm;
  return UNKNOWN_AXIS_GROUP;
};

interface ProgressEntryLike {
  progressType?: string | null;
}

/**
 * Detect whether a goal's targets span multiple Y-axis groups, with a cache that
 * survives the brief undefined window during session-progress refetches.
 *
 * Returns axisGroupList (sorted, stable across reorders/refetches) and the
 * derived `hasMixedMeasurementTypes` boolean.
 */
export const useMixedAxisDetection = (
  goalId: string,
  progress: (ProgressEntryLike | null)[] | null | undefined,
): { axisGroupList: string[]; hasMixedMeasurementTypes: boolean; detectionReady: boolean } => {
  const [cachedAxisGroups, setCachedAxisGroups] = useState<string[]>([]);

  const liveAxisGroups = progress
    ? [
        ...new Set(
          progress
            // Targets without a progressType are skipped — an absent type shouldn't
            // count as a distinct axis group and trigger the mixed-types restriction.
            .filter((p) => p?.progressType)
            .map((p) => axisGroupOf(p?.progressType)),
        ),
      ].sort((a, b) => a.localeCompare(b))
    : undefined;
  const axisGroupList = liveAxisGroups ?? cachedAxisGroups;
  // Detection is ready once we have either live data OR a cached set from a previous load.
  // Consumers use this to avoid rendering the wrong chart shape on the first frame.
  const detectionReady = liveAxisGroups !== undefined || cachedAxisGroups.length > 0;

  useLayoutEffect(() => {
    setCachedAxisGroups([]);
  }, [goalId]);

  const liveAxisGroupsKey = liveAxisGroups?.join("|");
  const cachedAxisGroupsKey = cachedAxisGroups.join("|");
  useEffect(() => {
    if (liveAxisGroups && liveAxisGroupsKey !== cachedAxisGroupsKey) {
      setCachedAxisGroups(liveAxisGroups);
    }
  }, [liveAxisGroupsKey, cachedAxisGroupsKey]);

  return { axisGroupList, hasMixedMeasurementTypes: axisGroupList.length > 1, detectionReady };
};
